import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, BarChart, Bar, ComposedChart, Area, Cell } from 'recharts';
// Generate realistic S&P 500 historical data (5 years: ~3000 to ~6800)
const generateSPXHistory = () => {
  const data = [];
  const startPrice = 3000;
  const endPrice = 6791.69;
  const days = 260 * 5; // 5 years of trading days
  for (let i = 0; i < days; i++) {
    const progress = i / days;
    const basePrice = startPrice + (endPrice - startPrice) * progress;
    const volatility = basePrice * 0.015;
    const price = basePrice + (Math.random() - 0.5) * volatility;
    data.push({
      day: i,
      price: Number(price.toFixed(2)),
      ma50: price * (1 - 0.01 + Math.random() * 0.02),
      ma200: price * (1 - 0.03 + Math.random() * 0.02)
    });
  }
  return data;
};
// Generate realistic commodity historical prices
const generateCommodityHistory = () => {
  const data = [];
  const months = 60; // 5 years
  let gold = 1700; // Started around $1700/oz
  let oil = 60; // WTI started around $60/bbl
  let copper = 3.5; // Started around $3.50/lb
  for (let i = 0; i < months; i++) {
    // Gold trending up to current ~$4076
    gold += (4076 - 1700) / months + (Math.random() - 0.5) * 30;
    // Oil volatile, ended at ~$61.72
    oil += (61.72 - 60) / months + (Math.random() - 0.5) * 5;
    // Copper trending up to ~$5.20
    copper += (5.199 - 3.5) / months + (Math.random() - 0.5) * 0.15;
    data.push({
      month: i,
      gold: Number(Math.max(gold, 1500).toFixed(2)),
      oil: Number(Math.max(oil, 40).toFixed(2)),
      copper: Number(Math.max(copper, 3).toFixed(2))
    });
  }
  return data;
};
// Generate realistic FX correlation matrix
const generateFXCorrelation = () => {
  const pairs = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD'];
  const data = [];
  // Realistic correlation patterns
  const correlations = {
    'EUR/USD-EUR/USD': 1.0,
    'EUR/USD-GBP/USD': 0.72,
    'EUR/USD-USD/JPY': -0.45,
    'EUR/USD-AUD/USD': 0.58,
    'EUR/USD-USD/CAD': -0.38,
    'GBP/USD-GBP/USD': 1.0,
    'GBP/USD-USD/JPY': -0.41,
    'GBP/USD-AUD/USD': 0.54,
    'GBP/USD-USD/CAD': -0.35,
    'USD/JPY-USD/JPY': 1.0,
    'USD/JPY-AUD/USD': -0.52,
    'USD/JPY-USD/CAD': 0.48,
    'AUD/USD-AUD/USD': 1.0,
    'AUD/USD-USD/CAD': -0.62,
    'USD/CAD-USD/CAD': 1.0
  };
  pairs.forEach(pair1 => {
    pairs.forEach(pair2 => {
      const key = `${pair1}-${pair2}`;
      const reverseKey = `${pair2}-${pair1}`;
      const correlation = correlations[key] || correlations[reverseKey] || 0;
      data.push({
        pair1,
        pair2,
        correlation: Number(correlation.toFixed(2))
      });
    });
  });
  return data;
};
// Generate realistic volatility term structure (VIX-like)
const generateVolTermStructure = () => {
  const data = [];
  const tenors = ['1W', '1M', '3M', '6M', '1Y', '2Y'];
  const baseVols = [16.5, 17.2, 18.5, 19.8, 21.2, 22.5]; // Realistic term structure
  tenors.forEach((tenor, idx) => {
    data.push({
      tenor,
      impliedVol: Number(baseVols[idx].toFixed(2)),
      historicalVol: Number((baseVols[idx] * 0.92).toFixed(2)) // Historical typically lower
    });
  });
  return data;
};
// Generate realistic sector rotation data based on 2024 market
const generateSectorRotation = () => {
  const sectorData = [{
    sector: 'Technology',
    momentum: 15.2
  }, {
    sector: 'Healthcare',
    momentum: 8.4
  }, {
    sector: 'Financials',
    momentum: 12.1
  }, {
    sector: 'Energy',
    momentum: -3.8
  }, {
    sector: 'Consumer',
    momentum: 6.7
  }, {
    sector: 'Industrials',
    momentum: 10.3
  }];
  return sectorData.map(item => ({
    ...item,
    valueScore: 50 + Math.random() * 30,
    performance: item.momentum + (Math.random() - 0.5) * 3
  }));
};
// Generate realistic risk metrics
const generateRiskMetrics = () => {
  const data = [];
  for (let i = 0; i < 120; i++) {
    const baseVolatility = 14 + Math.sin(i / 10) * 4; // Cyclical volatility
    data.push({
      week: i,
      sharpe: Number((1.5 + Math.sin(i / 15) * 0.4).toFixed(2)),
      maxDrawdown: Number((-8 - Math.abs(Math.sin(i / 12)) * 5).toFixed(2)),
      volatility: Number(baseVolatility.toFixed(2)),
      var95: Number((-2.5 - Math.random() * 1.5).toFixed(2))
    });
  }
  return data;
};
// Generate realistic equity indices comparison
const generateIndicesComparison = () => {
  const data = [];
  const days = 250; // ~1 year
  // Realistic starting points normalized to 100
  let spx = 100;
  let dji = 100;
  let ndx = 100;
  let ftse = 100;
  // Realistic annual returns: SPX ~26%, NDX ~30%, DJI ~18%, FTSE ~8%
  const spxDaily = Math.pow(1.26, 1 / 250);
  const djDaily = Math.pow(1.18, 1 / 250);
  const ndxDaily = Math.pow(1.3, 1 / 250);
  const ftseDaily = Math.pow(1.08, 1 / 250);
  for (let i = 0; i < days; i++) {
    spx *= spxDaily * (1 + (Math.random() - 0.5) * 0.015);
    dji *= djDaily * (1 + (Math.random() - 0.5) * 0.012);
    ndx *= ndxDaily * (1 + (Math.random() - 0.5) * 0.02);
    ftse *= ftseDaily * (1 + (Math.random() - 0.5) * 0.01);
    data.push({
      day: i,
      SPX: Number(spx.toFixed(2)),
      DJI: Number(dji.toFixed(2)),
      NDX: Number(ndx.toFixed(2)),
      FTSE: Number(ftse.toFixed(2))
    });
  }
  return data;
};
// Generate realistic crypto market cap dominance
const generateCryptoDominance = () => [{
  name: 'Bitcoin',
  value: 58.3,
  color: '#F7931A'
}, {
  name: 'Ethereum',
  value: 18.7,
  color: '#627EEA'
}, {
  name: 'Stablecoins',
  value: 12.4,
  color: '#26A17B'
}, {
  name: 'Altcoins',
  value: 10.6,
  color: '#60A5FA'
}];
export function QuantVisualizations() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [spxHistory, setSpxHistory] = useState(generateSPXHistory());
  const [commodityHistory, setCommodityHistory] = useState(generateCommodityHistory());
  const [indicesComparison, setIndicesComparison] = useState(generateIndicesComparison());
  const [volTermStructure, setVolTermStructure] = useState(generateVolTermStructure());
  const [sectorRotation, setSectorRotation] = useState(generateSectorRotation());
  const [riskMetrics, setRiskMetrics] = useState(generateRiskMetrics());
  const fxCorrelation = generateFXCorrelation();
  const [cryptoDominance, setCryptoDominance] = useState(generateCryptoDominance());
  // Continuously update ALL data to make charts feel alive
  useEffect(() => {
    const interval = setInterval(() => {
      // Update S&P 500 data - shift and add new point
      setSpxHistory(prev => {
        const newData = [...prev];
        const lastPoint = newData[newData.length - 1];
        const newPrice = lastPoint.price + (Math.random() - 0.48) * 30;
        newData.shift();
        newData.push({
          day: lastPoint.day + 1,
          price: Number(newPrice.toFixed(2)),
          ma50: newPrice * (1 - 0.01 + Math.random() * 0.02),
          ma200: newPrice * (1 - 0.03 + Math.random() * 0.02)
        });
        return newData;
      });
      // Update commodity data
      setCommodityHistory(prev => {
        const newData = [...prev];
        const lastPoint = newData[newData.length - 1];
        newData.shift();
        newData.push({
          month: lastPoint.month + 1,
          gold: Number((lastPoint.gold + (Math.random() - 0.5) * 15).toFixed(2)),
          oil: Number((lastPoint.oil + (Math.random() - 0.5) * 3).toFixed(2)),
          copper: Number((lastPoint.copper + (Math.random() - 0.5) * 0.15).toFixed(2))
        });
        return newData;
      });
      // Update indices comparison
      setIndicesComparison(prev => {
        const newData = [...prev];
        const lastPoint = newData[newData.length - 1];
        newData.shift();
        newData.push({
          day: lastPoint.day + 1,
          SPX: Number((lastPoint.SPX + (Math.random() - 0.48) * 1.5).toFixed(2)),
          DJI: Number((lastPoint.DJI + (Math.random() - 0.48) * 1.2).toFixed(2)),
          NDX: Number((lastPoint.NDX + (Math.random() - 0.48) * 2).toFixed(2)),
          FTSE: Number((lastPoint.FTSE + (Math.random() - 0.5) * 1).toFixed(2))
        });
        return newData;
      });
      // Update volatility term structure
      setVolTermStructure(prev => prev.map(item => ({
        ...item,
        impliedVol: Number((item.impliedVol + (Math.random() - 0.5) * 0.5).toFixed(2)),
        historicalVol: Number((item.historicalVol + (Math.random() - 0.5) * 0.4).toFixed(2))
      })));
      // Update sector rotation
      setSectorRotation(prev => prev.map(sector => ({
        ...sector,
        momentum: Number((sector.momentum + (Math.random() - 0.5) * 0.5).toFixed(1)),
        valueScore: sector.valueScore + (Math.random() - 0.5) * 2,
        performance: Number((sector.performance + (Math.random() - 0.5) * 0.3).toFixed(1))
      })));
      // Update risk metrics
      setRiskMetrics(prev => {
        const newData = [...prev];
        const lastPoint = newData[newData.length - 1];
        newData.shift();
        newData.push({
          week: lastPoint.week + 1,
          sharpe: Number((1.5 + Math.sin(lastPoint.week / 15) * 0.4 + (Math.random() - 0.5) * 0.1).toFixed(2)),
          maxDrawdown: Number((-8 - Math.abs(Math.sin(lastPoint.week / 12)) * 5 + (Math.random() - 0.5) * 0.5).toFixed(2)),
          volatility: Number((14 + Math.sin(lastPoint.week / 10) * 4 + (Math.random() - 0.5) * 0.5).toFixed(2)),
          var95: Number((-2.5 - Math.random() * 1.5).toFixed(2))
        });
        return newData;
      });
      // Update crypto dominance
      setCryptoDominance(prev => prev.map(item => ({
        ...item,
        value: Number(Math.max(5, Math.min(70, item.value + (Math.random() - 0.5) * 1)).toFixed(1))
      })));
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  return <section ref={ref} className="w-full py-16 md:py-32 px-4 md:px-6 bg-navy-900 overflow-hidden" data-id="element-235">
      <div className="max-w-7xl mx-auto" data-id="element-236">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 text-center text-white px-2" data-id="element-237">
          Historical Market Analysis
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center text-slate text-sm md:text-base mb-8 md:mb-16 max-w-3xl mx-auto px-4" data-id="element-238">
          Comprehensive quantitative analysis across global markets with
          historical data, correlations, and risk metrics
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8" data-id="element-239">
          {/* S&P 500 Historical with Moving Averages */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 shadow-lg w-full" data-id="element-240">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-241">
              S&P 500 Historical Performance
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-242">
              5-year trajectory: $3,000 → $6,792 (+126%)
            </p>
            <div className="w-full overflow-hidden" data-id="element-243">
              <ResponsiveContainer width="100%" height={250} data-id="element-244">
                <LineChart data={spxHistory} data-id="element-245">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-246" />
                  <XAxis dataKey="day" stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-247" />
                  <YAxis stroke="#CBD5E1" domain={[2800, 7000]} tick={{
                  fontSize: 10
                }} data-id="element-248" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-249" />
                  <Line type="monotone" dataKey="price" stroke="#60A5FA" strokeWidth={2} dot={false} name="SPX" isAnimationActive={false} data-id="element-250" />
                  <Line type="monotone" dataKey="ma50" stroke="#F59E0B" strokeWidth={1} strokeDasharray="5 5" dot={false} name="50 MA" isAnimationActive={false} data-id="element-251" />
                  <Line type="monotone" dataKey="ma200" stroke="#10B981" strokeWidth={1} strokeDasharray="5 5" dot={false} name="200 MA" isAnimationActive={false} data-id="element-252" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Global Indices Comparison */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 shadow-lg w-full" data-id="element-253">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-254">
              Global Equity Indices Comparison
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-255">
              1-year normalized performance (NDX +30%, SPX +26%, DJI +18%, FTSE
              +8%)
            </p>
            <div className="w-full overflow-hidden" data-id="element-256">
              <ResponsiveContainer width="100%" height={250} data-id="element-257">
                <LineChart data={indicesComparison} data-id="element-258">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-259" />
                  <XAxis dataKey="day" stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-260" />
                  <YAxis stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-261" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-262" />
                  <Line type="monotone" dataKey="SPX" stroke="#60A5FA" strokeWidth={2} dot={false} name="S&P 500" isAnimationActive={false} data-id="element-263" />
                  <Line type="monotone" dataKey="DJI" stroke="#10B981" strokeWidth={2} dot={false} name="Dow Jones" isAnimationActive={false} data-id="element-264" />
                  <Line type="monotone" dataKey="NDX" stroke="#F59E0B" strokeWidth={2} dot={false} name="NASDAQ" isAnimationActive={false} data-id="element-265" />
                  <Line type="monotone" dataKey="FTSE" stroke="#EF4444" strokeWidth={2} dot={false} name="FTSE 100" isAnimationActive={false} data-id="element-266" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Commodity Price Trends */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 shadow-lg w-full" data-id="element-267">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-268">
              Commodity Historical Prices
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-269">
              5-year trends (Gold: $1,700→$4,076, Oil: $60→$62, Copper:
              $3.50→$5.20)
            </p>
            <div className="w-full overflow-hidden" data-id="element-270">
              <ResponsiveContainer width="100%" height={250} data-id="element-271">
                <LineChart data={commodityHistory} data-id="element-272">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-273" />
                  <XAxis dataKey="month" stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-274" />
                  <YAxis stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-275" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-276" />
                  <Line type="monotone" dataKey="gold" stroke="#F59E0B" strokeWidth={2} dot={false} name="Gold ($/oz)" isAnimationActive={false} data-id="element-277" />
                  <Line type="monotone" dataKey="oil" stroke="#EF4444" strokeWidth={2} dot={false} name="WTI Oil ($/bbl)" isAnimationActive={false} data-id="element-278" />
                  <Line type="monotone" dataKey="copper" stroke="#14B8A6" strokeWidth={2} dot={false} name="Copper ($/lb)" isAnimationActive={false} data-id="element-279" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Volatility Term Structure */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 w-full" data-id="element-280">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-281">
              Volatility Term Structure
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-282">
              VIX-style implied vs historical volatility (upward sloping term
              structure)
            </p>
            <div className="w-full overflow-hidden" data-id="element-283">
              <ResponsiveContainer width="100%" height={250} data-id="element-284">
                <BarChart data={volTermStructure} data-id="element-285">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-286" />
                  <XAxis dataKey="tenor" stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-287" />
                  <YAxis stroke="#CBD5E1" domain={[0, 25]} tick={{
                  fontSize: 10
                }} data-id="element-288" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-289" />
                  <Bar dataKey="impliedVol" fill="#60A5FA" name="Implied Vol %" isAnimationActive={false} data-id="element-290" />
                  <Bar dataKey="historicalVol" fill="#14B8A6" name="Historical Vol %" isAnimationActive={false} data-id="element-291" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Sector Rotation */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 w-full" data-id="element-292">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-293">
              Sector Momentum Analysis
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-294">
              2024 YTD performance (Tech +15%, Financials +12%, Energy -4%)
            </p>
            <div className="w-full overflow-hidden" data-id="element-295">
              <ResponsiveContainer width="100%" height={250} data-id="element-296">
                <BarChart data={sectorRotation} layout="vertical" data-id="element-297">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-298" />
                  <XAxis type="number" stroke="#CBD5E1" domain={[-10, 20]} tick={{
                  fontSize: 10
                }} data-id="element-299" />
                  <YAxis dataKey="sector" type="category" stroke="#CBD5E1" width={70} tick={{
                  fontSize: 10
                }} data-id="element-300" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-301" />
                  <Bar dataKey="momentum" name="Momentum %" isAnimationActive={false} data-id="element-302">
                    {sectorRotation.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.momentum > 0 ? '#10B981' : '#EF4444'} data-id="element-303" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Risk Metrics Over Time */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 w-full" data-id="element-304">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-305">
              Portfolio Risk Metrics
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-306">
              Rolling risk analysis (Sharpe ~1.5, Max DD -13%, Vol ~14%)
            </p>
            <div className="w-full overflow-hidden" data-id="element-307">
              <ResponsiveContainer width="100%" height={250} data-id="element-308">
                <ComposedChart data={riskMetrics} data-id="element-309">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-310" />
                  <XAxis dataKey="week" stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-311" />
                  <YAxis stroke="#CBD5E1" tick={{
                  fontSize: 10
                }} data-id="element-312" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-313" />
                  <Line type="monotone" dataKey="sharpe" stroke="#10B981" strokeWidth={2} dot={false} name="Sharpe Ratio" isAnimationActive={false} data-id="element-314" />
                  <Line type="monotone" dataKey="maxDrawdown" stroke="#EF4444" strokeWidth={2} dot={false} name="Max Drawdown %" isAnimationActive={false} data-id="element-315" />
                  <Area type="monotone" dataKey="volatility" fill="#60A5FA20" stroke="#60A5FA" name="Volatility %" isAnimationActive={false} data-id="element-316" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          {/* Crypto Market Dominance */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.9
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 w-full" data-id="element-317">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-318">
              Crypto Market Cap Dominance
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-319">
              Current distribution of $3.5T total market cap
            </p>
            <div className="flex items-center justify-center min-h-[250px]" data-id="element-320">
              <div className="w-full space-y-3 md:space-y-4" data-id="element-321">
                {cryptoDominance.map(item => <div key={item.name} className="space-y-1 md:space-y-2" data-id="element-322">
                    <div className="flex justify-between text-xs md:text-sm" data-id="element-323">
                      <span className="text-white font-semibold" data-id="element-324">
                        {item.name}
                      </span>
                      <motion.span key={item.value} initial={{
                    scale: 1
                  }} animate={{
                    scale: [1, 1.2, 1]
                  }} transition={{
                    duration: 0.3
                  }} className="text-slate" data-id="element-325">
                        {item.value}%
                      </motion.span>
                    </div>
                    <div className="w-full bg-navy-700 rounded-full h-2 md:h-3 overflow-hidden" data-id="element-326">
                      <motion.div animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 0.5
                  }} className="h-full rounded-full" style={{
                    backgroundColor: item.color
                  }} data-id="element-327" />
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
          {/* FX Pairs Correlation Heatmap */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 1.0
        }} className="bg-navy-800 border-2 border-navy-700 rounded-lg p-3 md:p-6 hover:border-steel transition-all duration-300 w-full" data-id="element-328">
            <h3 className="text-base md:text-xl font-bold mb-2 md:mb-4 text-white" data-id="element-329">
              FX Correlation Matrix
            </h3>
            <p className="text-xs text-slate mb-2 md:mb-4" data-id="element-330">
              Cross-currency pair correlations (EUR/GBP: 0.72, USD/JPY inverse)
            </p>
            <div className="overflow-x-auto -mx-3 md:mx-0 px-3 md:px-0" data-id="element-331">
              <div className="min-w-[400px]" data-id="element-332">
                <table className="w-full text-xs" data-id="element-333">
                  <thead data-id="element-334">
                    <tr data-id="element-335">
                      <th className="p-1 md:p-2" data-id="element-336"></th>
                      {['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD'].map(pair => <th key={pair} className="p-1 md:p-2 text-slate text-center text-[10px] md:text-xs" data-id="element-337">
                          {pair}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-338">
                    {['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD'].map(pair1 => <tr key={pair1} data-id="element-339">
                        <td className="p-1 md:p-2 text-slate font-semibold text-[10px] md:text-xs" data-id="element-340">
                          {pair1}
                        </td>
                        {fxCorrelation.filter(d => d.pair1 === pair1).map((cell, j) => <td key={j} className="p-1 md:p-2 text-center" data-id="element-341">
                              <div className="rounded px-1 md:px-2 py-0.5 md:py-1 font-semibold text-[10px] md:text-xs" style={{
                        backgroundColor: cell.correlation > 0.5 ? '#10B98140' : cell.correlation < -0.3 ? '#EF444440' : '#60A5FA20',
                        color: '#FFFFFF'
                      }} data-id="element-342">
                                {cell.correlation.toFixed(2)}
                              </div>
                            </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}